<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { debounce } from 'lodash'
import { useWindowSize } from '@vueuse/core'
import Button from 'primevue/button'
import Drawer from 'primevue/drawer'
import ReservationsMiniCal from './ReservationsMiniCal.vue'
import ReservationsFormsSection from './ReservationsFormsSection.vue'
import ReservationsColleaguesSection from './ReservationsColleaguesSection.vue'
import { useNeoStore } from '../../../../../state/neo.js'

const neoStore = useNeoStore()
onMounted(() => { neoStore.panelWidth = 260 })
onUnmounted(() => { neoStore.panelWidth = 0 })

// AppTopBar only renders #topbar-left-page-actions when !isMobile (width >= 1024) —
// disable the Teleport instead of targeting a selector that may not exist yet.
const { width } = useWindowSize()
const isTopbarSearchTargetAvailable = computed(() => width.value >= 1024)

const props = defineProps({
    reservationForms:    { type: Array,   default: () => [] },
    allEmployees:        { type: Array,   default: () => [] },
    reservations:        { type: Array,   default: () => [] },
    visibleFormIds:      { type: Set,     default: () => new Set() },
    visibleColleagueIds: { type: Set,     default: () => new Set() },
    currentMonth:        Object,
    mobileOpen:          { type: Boolean, default: false },
})

const emit = defineEmits([
    'toggle-form',
    'toggle-colleague',
    'prev-month',
    'next-month',
    'new-reservation',
    'update:searchQuery',
    'close',
])

const drawerVisible = computed({
    get: () => props.mobileOpen,
    set: (val) => { if (!val) emit('close') },
})

const searchQuery = ref('')
const emitSearch = debounce((val) => emit('update:searchQuery', val), 200)
watch(searchQuery, emitSearch)

function clearSearch() {
    emitSearch.cancel()
    searchQuery.value = ''
    emit('update:searchQuery', '')
}
</script>

<template>
    <!-- Vyhledávání přesunuté do topbaru (desktop), úplně vlevo — šířka zachovaná stejná jako v sidebaru -->
    <Teleport to="#topbar-left-page-actions" :disabled="!isTopbarSearchTargetAvailable">
        <div class="relative w-[232px] hidden lg:block">
            <i class="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 !text-[13px]"/>
            <input
                v-model="searchQuery"
                :placeholder="$t('reservations.search_placeholder')"
                class="w-full pl-8 py-1.5 rounded-xl text-[13px] border bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-colors"
                :class="searchQuery ? 'pr-7' : 'pr-3'"
            />
            <Button
                v-if="searchQuery"
                icon="pi pi-times"
                text rounded size="small"
                @click="clearSearch"
                class="!absolute !right-1 !top-1/2 !-translate-y-1/2 !w-6 !h-6 !text-neutral-400"
            />
        </div>
    </Teleport>

    <!-- Desktop sidebar (lg+) -->
    <div class="w-[260px] flex-shrink-0 bg-white dark:bg-neutral-900 border-r border-neutral-100 dark:border-neutral-800 hidden lg:flex flex-col h-dvh overflow-y-auto pt-14">
        <div class="flex flex-col gap-3 px-3.5 pt-3.5">

            <Button :label="$t('reservations.btn_new')" icon="pi pi-plus" class="w-full justify-center" @click="emit('new-reservation')"/>

            <ReservationsMiniCal
                v-if="currentMonth"
                :current-month="currentMonth"
                :reservations="reservations"
                :visible-form-ids="visibleFormIds"
                @prev="emit('prev-month')"
                @next="emit('next-month')"
            />

            <ReservationsFormsSection
                :reservation-forms="reservationForms"
                :reservations="reservations"
                :visible-form-ids="visibleFormIds"
                @toggle-form="(id) => emit('toggle-form', id)"
            />

            <ReservationsColleaguesSection
                :all-employees="allEmployees"
                :visible-colleague-ids="visibleColleagueIds"
                @toggle-colleague="(id) => emit('toggle-colleague', id)"
            />

        </div>
    </div>

    <!-- Mobilní Drawer (< lg) -->
    <Drawer v-model:visible="drawerVisible" position="left" class="!w-[280px] lg:hidden" @hide="emit('close')">
        <template #header>
            <span class="text-sm font-semibold text-neutral-800 dark:text-neutral-200">{{ $t('reservations.filter_title') }}</span>
        </template>

        <div class="flex flex-col gap-3">

            <div class="relative">
                <i class="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 !text-[13px]"/>
                <input
                    v-model="searchQuery"
                    :placeholder="$t('reservations.search_placeholder')"
                    class="w-full pl-8 py-1.5 rounded-xl text-[13px] border bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-colors"
                    :class="searchQuery ? 'pr-7' : 'pr-3'"
                />
                <Button
                    v-if="searchQuery"
                    icon="pi pi-times"
                    text rounded size="small"
                    @click="clearSearch"
                    class="!absolute !right-1 !top-1/2 !-translate-y-1/2 !w-6 !h-6 !text-neutral-400"
                />
            </div>

            <Button :label="$t('reservations.btn_new')" icon="pi pi-plus" class="w-full justify-center" @click="emit('new-reservation')"/>

            <ReservationsMiniCal
                v-if="currentMonth"
                :current-month="currentMonth"
                :reservations="reservations"
                :visible-form-ids="visibleFormIds"
                @prev="emit('prev-month')"
                @next="emit('next-month')"
            />

            <ReservationsFormsSection
                :reservation-forms="reservationForms"
                :reservations="reservations"
                :visible-form-ids="visibleFormIds"
                @toggle-form="(id) => emit('toggle-form', id)"
            />

            <ReservationsColleaguesSection
                :all-employees="allEmployees"
                :visible-colleague-ids="visibleColleagueIds"
                @toggle-colleague="(id) => emit('toggle-colleague', id)"
            />

        </div>
    </Drawer>
</template>
